const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api'
const AZURE_FUNCTION_KEY = import.meta.env.VITE_AZURE_FUNCTION_KEY as string | undefined

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

let authToken: string | null = null

export function setAuthToken(token: string | null) {
  authToken = token
}

export class ApiError extends Error {
  status: number
  payload?: unknown

  constructor(message: string, status: number, payload?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

interface RequestOptions extends RequestInit {
  skipAuth?: boolean
}

async function request<T>(path: string, method: HttpMethod, body?: unknown, options: RequestOptions = {}): Promise<T> {
  const { headers: optionHeaders, skipAuth, ...restOptions } = options

  const headers = new Headers({
    Accept: 'application/json',
    ...(body instanceof FormData
      ? {}
      : { 'Content-Type': 'application/json' }),
  })

  if (optionHeaders) {
    const extra = new Headers(optionHeaders)
    extra.forEach((value, key) => headers.set(key, value))
  }

  if (authToken && !skipAuth) {
    headers.set('Authorization', `Bearer ${authToken}`)
  }

  // Add Azure Function key if configured
  if (AZURE_FUNCTION_KEY) {
    headers.set('x-functions-key', AZURE_FUNCTION_KEY)
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...restOptions,
    method,
    headers,
    body: body instanceof FormData ? body : body ? JSON.stringify(body) : undefined,
  })

  if (response.status === 204) {
    return undefined as T
  }

  const contentType = response.headers.get('content-type') ?? ''
  const isJson = contentType.includes('application/json')
  const payload = isJson ? await response.json() : await response.text()

  if (!response.ok) {
    let message = 'Request failed'
    if (isJson && payload && typeof payload === 'object' && 'message' in payload) {
      const maybeMessage = (payload as Record<string, unknown>).message
      if (typeof maybeMessage === 'string') {
        message = maybeMessage
      }
    }

    throw new ApiError(message, response.status, payload)
  }

  return payload as T
}

export function get<T>(path: string, options?: RequestOptions) {
  return request<T>(path, 'GET', undefined, options)
}

export function post<T>(path: string, body?: unknown, options?: RequestOptions) {
  return request<T>(path, 'POST', body, options)
}

export function put<T>(path: string, body?: unknown, options?: RequestOptions) {
  return request<T>(path, 'PUT', body, options)
}

export function patch<T>(path: string, body?: unknown, options?: RequestOptions) {
  return request<T>(path, 'PATCH', body, options)
}

export function del<T>(path: string, options?: RequestOptions) {
  return request<T>(path, 'DELETE', undefined, options)
}
